import React, {
  MouseEvent,
} from 'react';
import {
  useNavigate,
} from 'react-router-dom';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  FaxViewDTO,
} from 'dtos';
import {
  formatContact,
} from '@populateui/utils';
import Name from 'pages/Dashboard/components/Name';
import {
  useMedia,
} from 'core/hooks';
import isNil from 'lodash/isNil';
import FaxSelect from 'pages/Dashboard/pages/Fax/FaxSelect';
import {
  faxEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';

type PatientCellProps = {
  patient?: FaxViewDTO['patient'];
};

function PatientCell({ patient }: PatientCellProps) {
  const { getMediaPathById } = useMedia();
  const navigate = useNavigate();
  const hasPatient = !isNil(patient);
  const patientId = patient?.patientId ?? 0;
  const link = hasPatient ? getPatientChartPath(patient?.patientId) : undefined;

  return (
    <Name
      link={isNil(link) ? undefined : {
        to: link,
        onClick: (event: MouseEvent<HTMLAnchorElement>) => {
          event.stopPropagation();
          navigate(link);
        },
      }}
      {...patient?.formattedNames}
      placeholder="Not Assigned"
      image={hasPatient ? { src: getMediaPathById(patientId) ?? undefined } : undefined}
    />
  );
}

type FromCellProps = {
  original: FaxViewDTO;
};

function FromCell({ original }: FromCellProps) {
  const [name, phone] = formatContact(original.from ?? '');

  return (
    <div>
      <Name
        name={name}
        image
      >
        {phone}
      </Name>
    </div>
  );
}

const columns: ColumnDef<FaxViewDTO>[] = [
  {
    id: 'status',
    accessorKey: 'category',
    header: 'Status',
    size: 160,
    cell: ({ row: { original: { category, direction, status, id, errorMessage } } }) => (
      <FaxSelect
        id={id ?? 0}
        status={status ?? ''}
        category={category ?? ''}
        errorMessage={errorMessage}
        onChange={() => { faxEventChannel.emit('onFaxChange'); }}
        direction={direction ?? ''}
      />
    ),
  }, {
    id: 'date',
    accessorKey: 'date',
    header: 'Sent on',
    size: 100,
    cell: ({ row: { original: { date } } }) => (
      <span>
        {date}
      </span>
    ),
  }, {
    id: 'from',
    accessorKey: 'from',
    header: 'From',
    meta: { autoSized: true },
    minSize: 200,
    cell: ({ row: { original } }) => <FromCell original={original} />,
  }, {
    id: 'to',
    accessorKey: 'to',
    header: 'To',
    meta: { autoSized: true },
    minSize: 200,
    cell: ({ row: { original: { to } } }) => {
      const [name, phone] = formatContact(to ?? '');
      const alt = name?.split(', ')?.reverse()?.join(' ') ?? '';
      return (
        <div>
          <Name name={name} image={{ alt }}>
            {phone}
          </Name>
        </div>
      );
    },
  }, {
    id: 'patientName',
    accessorKey: 'patientName',
    header: 'Patient',
    meta: { autoSized: true },
    minSize: 200,
    cell: ({ row: { original: { patient } } }) => <PatientCell patient={patient} />,
  },
];

export default columns;
